import React, { useState } from "react";
import { usePolarisRecaptcha } from "../hooks/usePolarisRecaptcha";
import { Link } from "react-router-dom";
import { buildApiUrl } from "../utils/apiConfig";
import "../styles/landing-page.css";
import "../styles/contact-page.css";
import PublicHeader from "./PublicHeader";
import PublicFooter from "./PublicFooter";
import { RecaptchaDisclosure } from "./RecaptchaDisclosure";
import { apiClient } from "../utils/apiClient";

interface FormErrors {
    email?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function NewsletterPage() {
    const { executeRecaptcha } = usePolarisRecaptcha();
    const [email, setEmail] = useState("");
    const [errors, setErrors] = useState<FormErrors>({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [serverError, setServerError] = useState("");

    function validate(): FormErrors {
        const errs: FormErrors = {};
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            errs.email = "Email address is required.";
        } else if (!EMAIL_REGEX.test(trimmedEmail)) {
            errs.email = "Please enter a valid email address.";
        }
        return errs;
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setServerError("");
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;

        setSubmitting(true);
        try {
            const recaptchaToken = await executeRecaptcha("newsletter");
            const res = await apiClient(buildApiUrl("api/newsletter/subscribe"), {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({
                    email: email.trim(),
                    recaptchaToken,
                }),
            });
            if (!res.ok) {
                // Never surface the raw response body to customers — map to friendly copy.
                setServerError(
                    res.status === 400 || res.status === 422
                        ? "Please enter a valid email address and try again."
                        : "We couldn't sign you up right now. Please try again in a little while."
                );
                return;
            }
            setSubmitted(true);
        } catch (err) {
            const recaptchaFailed =
                err instanceof Error && err.message === "recaptcha-not-ready";
            setServerError(
                recaptchaFailed
                    ? "We couldn't verify this request. Please refresh the page and try again."
                    : "We couldn't sign you up right now. Please try again in a little while."
            );
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <main className="contact-page landing-page newsletter-page">

            <PublicHeader />

            {/* Hero Banner */}
            <section className="contact-hero">
                <h1 className="contact-hero-heading">Newsletter</h1>
                <p className="contact-hero-breadcrumb">
                    <Link to="/">Home</Link>
                    <span className="contact-hero-breadcrumb-separator">&gt;</span>
                    Newsletter
                </p>
            </section>

            {/* Signup form */}
            <section className="contact-content">
                <div className="contact-content-inner contact-content-inner--single">
                    <div className="contact-form-card">
                        {submitted ? (
                            <div className="contact-success">
                                <div className="contact-success-check">&#10003;</div>
                                <h3 className="contact-success-heading">You&apos;re Subscribed</h3>
                                <p className="contact-success-text">
                                    Thanks for signing up! Keep an eye on your inbox for insights and resources
                                    for today&apos;s student-athlete landscape.
                                </p>
                            </div>
                        ) : (
                            <>
                                <h2 className="contact-form-heading">Get The Polaris Athlete Hub Newsletter</h2>
                                <p className="contact-success-text" style={{ marginBottom: 24 }}>
                                    Insights and resources for success in today&apos;s student-athlete landscape.
                                </p>
                                {serverError && <div className="contact-form-server-error">{serverError}</div>}
                                <form onSubmit={handleSubmit} noValidate>
                                    <div className="contact-form-group">
                                        <label className="contact-form-label">Your Email</label>
                                        <div className="contact-form-input-wrapper">
                                            <input
                                                type="email"
                                                className={`contact-form-input${errors.email ? " contact-form-input--error" : ""}`}
                                                placeholder="your email..."
                                                value={email}
                                                onChange={(e) => { setEmail(e.target.value); setErrors({}); }}
                                            />
                                            <span className="contact-form-field-icon">&#9993;</span>
                                        </div>
                                        {errors.email && <p className="contact-form-error">{errors.email}</p>}
                                    </div>
                                    <button type="submit" className="contact-form-submit-btn" disabled={submitting}>
                                        {submitting ? "Signing up..." : "Sign Up"}
                                        {!submitting && <span className="contact-form-submit-arrow">&rarr;</span>}
                                    </button>
                                    <RecaptchaDisclosure className="recaptcha-disclosure--on-dark" />
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </section>

            <PublicFooter />

        </main>
    );
}
